import {
  DepositorProxy,
  EthereumAddress,
  EthereumSigner,
  Hex,
  TBTC,
} from "@keep-network/tbtc-v2.ts"
import { useMutation } from "@tanstack/react-query"
import {
  Address,
  encodeAbiParameters,
  keccak256,
  parseAbiParameters,
} from "viem"

import { IS_MEZO_MAINNET } from "#/shared/constants"
import { MezoBridge } from "#/shared/constants/artifacts"
import { tbtcApi } from "#/shared/services"

const bitcoinDepositor: DepositorProxy = {
  getChainIdentifier: () => EthereumAddress.from(MezoBridge.address),
  // There is no need to implement this function because we reveal deposit via
  // tbtc-api.
  revealDeposit() {
    throw new Error("Unsupported method")
  },
}

export default function useGenerateTbtcDeposit() {
  return useMutation({
    mutationFn: async ({
      recipient,
      bitcoinRecoveryAddress,
      ethersSigner,
      loggedInUser,
    }: {
      recipient: Address
      bitcoinRecoveryAddress: string
      ethersSigner: EthereumSigner
      loggedInUser: string
    }) => {
      if (!ethersSigner) throw new Error("Ethers signer not defined")

      const tbtc = await (IS_MEZO_MAINNET
        ? TBTC.initializeMainnet(ethersSigner)
        : TBTC.initializeSepolia(ethersSigner))

      const extraData = Hex.from(
        keccak256(
          encodeAbiParameters(parseAbiParameters("address"), [recipient]),
        ),
      )

      const generatedDeposit = await tbtc.deposits.initiateDepositWithProxy(
        bitcoinRecoveryAddress,
        bitcoinDepositor,
        extraData,
      )

      const bitcoinDepositAddress = await generatedDeposit.getBitcoinAddress()
      const receipt = generatedDeposit.getReceipt()

      const revealData = {
        address: recipient,
        revealInfo: {
          depositor: receipt.depositor.identifierHex,
          blindingFactor: receipt.blindingFactor.toString(),
          walletPublicKeyHash: receipt.walletPublicKeyHash.toString(),
          refundPublicKeyHash: receipt.refundPublicKeyHash.toString(),
          refundLocktime: receipt.refundLocktime.toString(),
          extraData: receipt.extraData!.toString(),
        },
        metadata: {
          recipient,
        },
        application: "mezo",
        loggedInUser,
      }

      const revealSaved: boolean = await tbtcApi.saveReveal(revealData)
      if (!revealSaved)
        throw new Error("Reveal not saved properly in the database")

      return { deposit: generatedDeposit, bitcoinDepositAddress, revealData }
    },
  })
}
